<template>
  <div ref="catalogRoot" class="container">
    <Breadcrumb :links="pageBreadcrumbs"/>
    <clinicFilter v-model="filters" :description="filterDescription" :filters="clinicData?.filters" :title="pageTitle"
                  class="clinic-filter" :nearby-active="isNearbyActive" :nearby-pending="isNearbyPending"
                  @toggleNearby="toggleNearby" @updateFilters="onUpdateFilters"/>
    <div :class="{ 'clinic-list--loading': loading }" class="clinic-list">
      <ClinicListBefore
          v-model:view-type="viewType"
          :clinic-count="clinicCount"
          :show-map-toggle="mapsConfigured"
      />
      <div
          class="clinic-list__layout"
          :class="[`clinic-list__layout--${viewType}`, {'clinic-list__layout--no-map': !mapsConfigured || isEmptyList}]"
      >
        <div class="clinic-list__items">
          <UIListSkeleton v-if="isClinicsLoading" variant="clinic"/>
          <div v-else-if="clinicError" class="clinics-error" role="alert">
            <p>Не удалось загрузить клиники.</p>
            <Button is-button text="Повторить" @click="refreshClinics()"/>
          </div>
          <div v-else-if="clinics.length" class="clinics">
            <ClinicCard
                v-for="clinic in clinics"
                :key="clinic.id"
                :clinic="clinic"
                :is-active="selectedClinicId === clinic.id"
                @select="selectedClinicId = $event"
            />
          </div>
          <ClinicCatalogEmptyState v-else :clinics-url="allClinicsUrl" @search="focusSearch"/>
          <Pagination
            v-if="!isClinicsLoading && !clinicError && meta"
            :meta="meta"
            :page-href="paginationHref"
            @page-changed="(value: number) => page = value"
          />
          <UICollapsedText
              v-if="clinicData?.seo?.fullText && page === 1"
              class="clinic-list__seo-text"
              :content="clinicData.seo.fullText"
              :lines="8"
              :withoutEllipsis="true"
          />
        </div>
        <div v-if="mapsConfigured && !isEmptyList" class="clinic-list__map">
          <ClinicMap
              :filters="mapFilters"
              :view="true"
              height="var(--clinic-map-height, 100%)"
              :selected-clinic-id="selectedClinicId"
              :user-location="isNearbyActive ? nearbyCoords : null"
              @select="selectedClinicId = $event"
              @viewport="onMapViewport"
          />
        </div>
      </div>
    </div>
    <Transition name="fade">
      <div v-if="loading && viewType === 'map'" class="loading-overlay">
        <div class="loading-spinner"></div>
      </div>
    </Transition>
    <FaqSection
        v-if="clinicData?.seo?.faqs?.length && page === 1"
        :items="clinicData.seo.faqs"
        style="margin-bottom: 0;"
    />
    <FaqSection v-else-if="!pathHub && page === 1" :tag="'clinics'"/>
    <ClinicHubLinks
      v-if="showHubLinks"
      :hubs="clinicData?.hubs"
    />
  </div>
</template>

<script lang="ts" setup>
import type {Meta} from "~/types/request/meta";
import {computed, ref} from 'vue';
import type {TClinic, TClinicFilter, TClinicRaw} from "~/types/clinic";
import type {FilterSeo} from "~/types/filterSeo";
import {updateFiltersData} from "~/utils/doctorFilters";
import {
  clearGeoCatalogCityFilterCleared,
  markGeoCatalogCityFilterCleared,
} from '~/utils/geoCatalog';
import {useCityStore} from '~/stores/city';
import {
  buildClinicListUrl,
  type ClinicHubsIndex,
  type ClinicPathHub,
} from '~/utils/clinicListUrl';
import type {ClinicHubsPayload} from '~/components/Clinic/HubLinks.vue';
import {omitClinicPhoneFromList} from '~/utils/omitClinicPhone';
import {isYandexMapsConfigured} from '~/utils/yandexMaps';
import {
  isSameClinicMapBounds,
  parseClinicMapBounds,
  type ClinicMapBounds,
} from '~/utils/clinicMapBounds';

type TDictionary = { [key: string]: string };

interface FilterOption {
  id: string;
  slug: string;
  label: string;
  disabled: boolean;
  selected: boolean;
  type?: string | null;
  nameCases?: {
    [key: string]: string
  }
}

interface Filters {
  city: FilterOption[];
  district?: FilterOption[];
  metro?: FilterOption[];
}

const ALWAYS_OPEN_LANDING_PATH = '/clinics/kruglosutochnye/';

// Отдельная посадочная «круглосуточные клиники»: фильтр приходит не из query, а из самого URL,
// иначе канонический адрес страницы схлопнулся бы в /clinics/?isAlwaysOpen=y.
const props = defineProps<{
  alwaysOpenLanding?: boolean;
  pathHub?: ClinicPathHub;
}>();

const pathHub = computed(() => props.pathHub ?? null);

const config = useRuntimeConfig();
const router = useRouter();
const route = useRoute();
const {apiQuery} = useApi();
const {trackPageView} = usePageView();
const cityStore = useCityStore();

// Трекинг просмотра страницы каталога клиник
onMounted(() => {
  const slug = route.params.city ? String(route.params.city) : undefined;
  trackPageView('clinics_catalog', slug);
});
const page = ref<number>(Number(route.query.page) || 1);
const filters = ref<TClinicFilter>({
  type: 'all',
  city: '',
  district: '',
  metro: '',
  isAlwaysOpen: false,
  search: ''
});
const loading = ref(false);
const loadingTimeout = ref<NodeJS.Timeout | null>(null);
const mapsConfigured = computed(() => {
  const maps = (useRuntimeConfig().public as { yandexMaps?: { apikey?: string } }).yandexMaps;
  return isYandexMapsConfigured(maps?.apikey);
});
const viewType = ref<'list' | 'map'>(
    mapsConfigured.value && route.query.view === 'map' ? 'map' : 'list',
);
const shouldScrollOnPageChange = ref(false);
if (route.params.city && typeof route.params.city === 'string') {
  filters.value.city = route.params.city;
}
if (route.query.district && typeof route.query.district === 'string') {
  filters.value.district = route.query.district;
}
if (route.query.metro && typeof route.query.metro === 'string') {
  filters.value.metro = route.query.metro;
}
if (route.query.search && typeof route.query.search === 'string') {
  filters.value.search = route.query.search;
}
// if (route.query.type && typeof route.query.type === 'string' && (route.query.type === 'all' || route.query.type === 'online')) {
//   filters.value.type = route.query.type;
// }
if (route.query.isAlwaysOpen && typeof route.query.isAlwaysOpen === 'string') {
  filters.value.isAlwaysOpen = route.query.isAlwaysOpen === 'y';
}

if (props.alwaysOpenLanding) {
  filters.value.isAlwaysOpen = true;
}

if (props.pathHub) {
  const city = typeof route.params.city === 'string' ? route.params.city : '';
  filters.value.city = city;
  if (props.pathHub.type === 'kruglosutochno') {
    filters.value.isAlwaysOpen = true;
    filters.value.district = '';
    filters.value.metro = '';
  } else if (props.pathHub.type === 'metro') {
    filters.value.metro = props.pathHub.slug || '';
    filters.value.district = '';
    filters.value.isAlwaysOpen = false;
  } else {
    filters.value.district = props.pathHub.slug || '';
    filters.value.metro = '';
    filters.value.isAlwaysOpen = false;
  }
}

// Гео-фильтр «Рядом со мной»: координаты не попадают в URL и в фильтры,
// они только сортируют выдачу и добавляют расстояние в карточки.
const {coords: nearbyCoords, isActive: isNearbyActive, isPending: isNearbyPending, toggle: toggleNearby} = useNearbyLocation();
const selectedClinicId = ref<number | null>(null);

const nearbyQuery = computed(() => (isNearbyActive.value && nearbyCoords.value)
    ? {lat: nearbyCoords.value.lat, lng: nearbyCoords.value.lng}
    : {});
const mapBounds = ref<ClinicMapBounds | null>(null);
const mapBoundsQuery = computed(() => mapBounds.value ?? {});

const onMapViewport = (bounds: ClinicMapBounds) => {
  const next = parseClinicMapBounds(bounds);
  if (!next || isSameClinicMapBounds(next, mapBounds.value)) {
    return;
  }

  mapBounds.value = next;
  if (page.value !== 1) {
    page.value = 1;
  }
};

watch(() => [
  filters.value.city,
  filters.value.district,
  filters.value.metro,
  filters.value.search,
  filters.value.type,
  filters.value.isAlwaysOpen,
], () => {
  mapBounds.value = null;
});

watch(() => (isNearbyActive.value && nearbyCoords.value
    ? `${nearbyCoords.value.lat.toFixed(4)},${nearbyCoords.value.lng.toFixed(4)}`
    : ''), () => {
  mapBounds.value = null;
});

watch(selectedClinicId, async (clinicId) => {
  if (!clinicId || !import.meta.client) {
    return;
  }

  await nextTick();
  document.querySelector(`[data-clinic-id="${clinicId}"]`)
      ?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
});

const mapFilters = computed(() => ({...filters.value, ...nearbyQuery.value}));

const clinicsListCacheKey = computed(() => {
  const {city, district, metro, search, type, isAlwaysOpen} = filters.value;
  return [
    'clinics-list',
    page.value,
    city,
    district,
    metro,
    search,
    type,
    isAlwaysOpen ? '1' : '0',
    props.pathHub ? `${props.pathHub.type}:${props.pathHub.slug || ''}` : '',
    isNearbyActive.value && nearbyCoords.value
        ? `${nearbyCoords.value.lat.toFixed(4)},${nearbyCoords.value.lng.toFixed(4)}`
        : '',
    mapBounds.value
        ? [mapBounds.value.swLat, mapBounds.value.swLng, mapBounds.value.neLat, mapBounds.value.neLng].join(',')
        : '',
  ].join('-');
});

type TClinicsListResponse = {
  data: TClinic[];
  meta: Meta;
  filters: Filters;
  seo?: FilterSeo;
  hubs?: ClinicHubsPayload;
  hub?: { type: string; slug: string | null; label: string; count: number; path: string };
};

type TClinicsListRawResponse = Omit<TClinicsListResponse, 'data'> & { data: TClinicRaw[] };

const {data: clinicData, error: clinicError, refresh: refreshClinics, pending: clinicsPending} = await useAsyncData<TClinicsListResponse>(
    () => clinicsListCacheKey.value,
    async () => {
      const response = await apiQuery<TClinicsListRawResponse>('public/clinics', 'GET', {
        query: {
          page: page.value,
          ...filters.value,
          ...nearbyQuery.value,
          ...mapBoundsQuery.value,
          ...(props.pathHub
            ? {
                hubType: props.pathHub.type,
                hubSlug: props.pathHub.slug || undefined,
                with_hubs: false,
              }
            : {}),
        }
      });
      return {...response, data: omitClinicPhoneFromList(response.data)};
    },
    {
      immediate: true,
      watch: [clinicsListCacheKey],
    }
);

// Несуществующий город / хаб ниже порога → 404 вместо пустого листинга.
useThrowOnNotFound(clinicError as Ref<{ statusCode?: number } | null | undefined>, 'Страница не найдена');

const showHubLinks = computed(() =>
  !props.pathHub
  && !props.alwaysOpenLanding
  && page.value === 1
  && Boolean(clinicData.value?.hubs)
  && Boolean(filters.value.city)
  && !filters.value.district
  && !filters.value.metro
  && !filters.value.isAlwaysOpen
  && !filters.value.search
);

const clinics = computed<TClinic[]>(() => clinicData.value?.data || []);
const meta = computed<Meta | null>(() => clinicData.value?.meta || null);
const isClinicsLoading = computed(() => clinicsPending.value || loading.value);
const isEmptyList = computed(() => viewType.value === 'list' && !isClinicsLoading.value && !clinicError.value && !clinics.value.length);
const allClinicsUrl = computed(() => filters.value.city ? `/clinics/${filters.value.city}/` : '/clinics/');
const catalogRoot = ref<HTMLElement | null>(null);

const focusSearch = () => {
  const input = catalogRoot.value?.querySelector<HTMLInputElement>('.main-filter__search input');
  input?.focus();
  input?.select();
};

const citiesList = computed<TDictionary>(() => {
  if (!clinicData.value || !clinicData.value.filters || !clinicData.value.filters.city) {
    return {};
  }
  return clinicData.value?.filters?.city
      .filter(city => city.slug)
      .sort((a, b) => a.label.localeCompare(b.label))
      .reduce((acc, city) => {
        acc[city.slug] = city.label;
        return acc;
      }, {})
});

const clinicCount = computed<string>(() => {
  if (!meta || !meta.value || !meta.value.total) {
    return "";
  }
  return meta.value.total + " " + declineWord(meta.value.total, ["клиника", "клиники", "клиник"]);
});

const generateUrl = (newFilters: TClinicFilter, pageOverride?: number) => {
  return buildClinicListUrl({
    filters: newFilters,
    page: pageOverride ?? page.value,
    viewType: viewType.value,
    alwaysOpenLanding: props.alwaysOpenLanding,
    pathHub: props.pathHub,
    districtOptions: clinicData.value?.filters?.district ?? [],
    hubs: (clinicData.value?.hubs as ClinicHubsIndex | undefined) ?? null,
    alwaysOpenLandingPath: ALWAYS_OPEN_LANDING_PATH,
  });
};

const paginationHref = (pageNum: number) => generateUrl(filters.value, pageNum);

let isUpdating = false;
const isSyncingFromRoute = ref(false);
// Tracks the city the last committed filters used. A deep watch over a ref
// reuses the same proxy for new/old value, so `oldFilters.city` can't detect a
// city change — we compare against this instead.
let previousCity = filters.value.city;

const getRouteState = () => {
  const routeCity = route.params.city && typeof route.params.city === 'string' ? route.params.city : '';
  const hub = props.pathHub;

  let district = '';
  let metro = '';
  let isAlwaysOpen = false;

  if (hub?.type === 'okrug' || hub?.type === 'rayon') {
    district = hub.slug || '';
  } else if (hub?.type === 'metro') {
    metro = hub.slug || '';
  } else if (hub?.type === 'kruglosutochno') {
    isAlwaysOpen = true;
  } else {
    district = routeCity && route.query.district && typeof route.query.district === 'string' ? route.query.district : '';
    metro = routeCity && route.query.metro && typeof route.query.metro === 'string' ? route.query.metro : '';
    isAlwaysOpen = props.alwaysOpenLanding ? !routeCity : route.query.isAlwaysOpen === 'y';
  }

  const routeFilters: TClinicFilter = {
    ...filters.value,
    city: routeCity,
    type: 'all',
    district,
    metro,
    search: route.query.search && typeof route.query.search === 'string' ? route.query.search : '',
    isAlwaysOpen,
  };

  return {
    filters: routeFilters,
    page: Number(route.query.page) || 1,
    viewType: route.query.view === 'map' ? 'map' : 'list' as const
  };
};

watch(filters, async (newFilters) => {
  if (isSyncingFromRoute.value || isUpdating) {
    return;
  }

  const cityChanged = newFilters.city !== previousCity;
  const hadCity = !!previousCity;
  previousCity = newFilters.city;

  const filtersForUrl = cityChanged
      ? {...newFilters, district: '', metro: ''}
      : newFilters;

  if (cityChanged) {
    isUpdating = true;
    filters.value.district = '';
    filters.value.metro = '';
  }

  if (filtersForUrl.city) {
    clearGeoCatalogCityFilterCleared()
  } else if (hadCity) {
    markGeoCatalogCityFilterCleared()
  }

  loading.value = true;
  isUpdating = true;

  try {
    if (page.value !== 1) page.value = 1;
    await router.push(generateUrl(filtersForUrl));
    await refreshClinics();
    await nextTick();
  } catch (error) {
    console.error('Ошибка при загрузке:', error);
  } finally {
    loading.value = false;
    isUpdating = false;
  }
}, {deep: true});

watch(page, async () => {
  if (isUpdating || isSyncingFromRoute.value) return;
  loading.value = true;
  isUpdating = true;
  shouldScrollOnPageChange.value = true;

  try {
    await router.push(generateUrl(filters.value));
    await refreshClinics();
    await nextTick();
  } catch (error) {
    console.error('Ошибка при загрузке:', error);
  } finally {
    loading.value = false;
    isUpdating = false;
  }
});

watch(loading, (newLoading, oldLoading) => {
  // Скролл только когда загрузка завершилась и был запрос на скролл
  if (!newLoading && oldLoading && shouldScrollOnPageChange.value) {
    nextTick(() => {
      window.scrollTo({top: 0, behavior: 'smooth'});
      shouldScrollOnPageChange.value = false;
    });
  }
});

watch(mapsConfigured, (enabled) => {
  if (!enabled) {
    viewType.value = 'list';
  }
});

watch(viewType, () => {
  if (isSyncingFromRoute.value) return;

  router.push(generateUrl(filters.value));
});

watch(() => route.fullPath, async () => {
  if (isUpdating) {
    return;
  }

  const routeState = getRouteState();
  if (
      JSON.stringify(filters.value) === JSON.stringify(routeState.filters)
      && page.value === routeState.page
      && viewType.value === routeState.viewType
  ) {
    return;
  }

  isSyncingFromRoute.value = true;
  filters.value = routeState.filters;
  previousCity = routeState.filters.city;
  page.value = routeState.page;
  viewType.value = routeState.viewType;

  loading.value = true;

  try {
    await refreshClinics();
  } finally {
    loading.value = false;
    isSyncingFromRoute.value = false;
  }
});

// const cityTitle = computed(() => {
//   if (!filters.value.city || !citiesList.value) return '';
//   return citiesList.value[filters.value.city] || '';
// });
const cityTitle = computed(() => {
  if (!filters.value.city) return '';
  if (citiesList.value[filters.value.city]) {
    return citiesList.value[filters.value.city];
  }
  if (cityStore.current?.slug === filters.value.city) {
    return cityStore.current.name;
  }
  return '';
});
// const cityPrepositionalSingular = computed(() => {
//   if (!filters.value.city || !citiesList.value) return '';
//   const cities = clinicData.value?.filters?.city || [];
//   const selectedCity = cities.find(city => city.slug === filters.value.city);
//   return selectedCity?.nameCases?.prepositional_singular || `г. ${citiesList.value[filters.value.city]}` || '';
// })
const cityPrepositionalSingular = computed(() => {
  if (!filters.value.city) return '';
  const cities = clinicData.value?.filters?.city || [];
  const selectedCity = cities.find(city => city.slug === filters.value.city);
  if (selectedCity?.nameCases?.prepositional_singular) {
    return selectedCity.nameCases.prepositional_singular;
  }
  if (cityStore.current?.slug === filters.value.city && cityStore.prepositionalName) {
    return cityStore.prepositionalName;
  }
  if (citiesList.value[filters.value.city]) {
    return `г. ${citiesList.value[filters.value.city]}`;
  }
  return '';
})

const clinicCountText = computed(() => {
  if (!meta.value?.total) return '';
  return `${meta.value.total} ${declineWord(meta.value.total, ["клиника", "клиники", "клиник"])}`;
});

const fallbackPageTitle = computed(() => {
  // Посадочная по круглосуточным клиникам обязана иметь свой H1, иначе она дублирует
  // общий листинг заголовком и не отличается для поиска.
  const base = props.alwaysOpenLanding
      ? "Круглосуточные ветеринарные клиники"
      : "Ветеринарные клиники";

  return cityTitle.value ? `${base} в ${cityPrepositionalSingular.value}` : base;
});

const pageTitle = computed(() => clinicData.value?.seo?.h1 || fallbackPageTitle.value);

const seoTitle = computed(() => {
  if (clinicData.value?.seo?.metaTitle) {
    return clinicData.value.seo.metaTitle;
  }
  if (cityTitle.value) {
    return `Ветеринарные клиники в ${cityPrepositionalSingular.value} рядом со мной – ${clinicCountText.value}, цены, отзывы, адреса`;
  }
  return `Ветеринарные клиники с высоким рейтингом – ${clinicCountText.value}, цены и отзывы`;
});

const seoDescription = computed(() => {
  if (clinicData.value?.seo?.metaDescription) {
    return clinicData.value.seo.metaDescription;
  }
  if (cityTitle.value) {
    return `Ветеринарные клиники в ${cityPrepositionalSingular.value} – ${clinicCountText.value}, цены от 1000р., отзывы и рейтинги, адреса на карте, часы работы. Записывайся на прием онлайн.`;
  }
  return `Выбирай лучшие ветеринарные клиники – ${clinicCountText.value}, цены, рейтинги и отзывы. Удобный поиск ветеринарных клиник рядом с вами.`;
});

const pageBreadcrumbs = computed(() => {
  const crumbs: { name: string; link?: string }[] = [
    {name: 'Клиники', link: '/clinics/'},
  ];
  if (props.alwaysOpenLanding && !cityTitle.value) {
    crumbs.push({name: 'Круглосуточные'});
    return crumbs;
  }
  if (cityTitle.value && filters.value.city) {
    crumbs.push({
      name: cityTitle.value,
      link: clinicData.value?.hub ? `/clinics/${filters.value.city}/` : undefined,
    });
  }
  if (clinicData.value?.hub?.label) {
    crumbs.push({name: clinicData.value.hub.label});
  }
  return crumbs;
});

const filterDescription = computed(() => {
  if (clinicData.value?.seo?.subtitle) {
    return clinicData.value.seo.subtitle;
  }
  const base = "Выберите лучшее место лечения для вашего питомца";
  return cityTitle.value
      ? `${base} в ${cityPrepositionalSingular.value}`
      : `${base} по всей России`;
});

// Любые query-параметры на listing-странице (фильтры, пагинация, сортировка)
// создают URL-дубликат базового каталога — такие страницы не должны попадать
// в индекс, а каноническая версия указывает на чистый path без query.
const hasIndexBlockingQuery = computed(() => Object.keys(route.query).length > 0);

const clinicSeoRobots = computed(() => {
  if (clinicData.value?.seo?.robots) {
    return clinicData.value.seo.robots;
  }
  if (meta.value?.total === 0) {
    return 'noindex, follow'
  }
  if (hasIndexBlockingQuery.value) {
    return 'noindex, follow'
  }
  return undefined
})

const clinicSeoCanonical = computed(() => {
  if (clinicData.value?.seo?.canonical) {
    return clinicData.value.seo.canonical;
  }
  if (hasIndexBlockingQuery.value) {
    return `https://vetsy.ru${route.path}`;
  }
  return undefined;
});

useSeoMeta({
  robots: () => clinicSeoRobots.value,
})

useHead({
  title: () => seoTitle.value,
  meta: [
    {
      name: 'description',
      content: () => seoDescription.value
    },
    {
      property: 'og:title',
      content: () => seoTitle.value
    },
    {
      property: 'og:description',
      content: () => seoDescription.value
    }
  ],
  link: computed(() => clinicSeoCanonical.value ? [{ rel: 'canonical', href: clinicSeoCanonical.value }] : []),
});

useClinicListingSchema({
  title: pageTitle,
  clinics,
});

function onUpdateFilters(updatedFilters: Record<string, any>) {
  updateFiltersData(clinicData, updatedFilters);
}
</script>

<style lang="scss">
.clinic-title {
  color: $color-black;
  font-size: 48px;
  font-weight: 500;
  line-height: 52px;
  text-align: center;
  margin-top: 16px;

  @include media(sm) {
    font-size: 28px;
    line-height: 30px;
  }
}

.clinic-subtitle {
  color: $color-gray;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;
}

.clinic-list {
  position: relative;

  & .button {
    @include media(sm) {
      width: 100%;
    }
  }

  .clinic-card__cta .button {
    width: auto;
  }

  &__seo-text {
    margin-top: 40px;
  }

  .clinic-map__popup--bottom {
    transform: translateY(-50px);
  }
}

.clinic-filter {
  .main-filter__item._types {
    width: auto;
    min-width: 240px;
  }

  .base-select__wrap {
    min-width: 100%;
  }

  .main-filter__item--select-mobile-city .base-select__wrap {
    right: unset;
  }

  .base-select__btn.disabled {
    background-color: alpha($color-disabled, 0.33)
  }

  .main-filter__bottom._desktop.main-filter__bottom--service {
    display: flex;
    bottom: 0;
    position: relative;
    margin-top: 16px;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;

    .main-filter__clear {
      margin-top: 0;
    }
  }

  // Временно: на мобиле та же строка фильтров, что на десктопе (Район + Метро), без кнопки «Все фильтры».
  // Прежний вариант — mobileFilters + main-filter__wrap-mobile (см. Clinic/Filter.vue).
  @include media(sm) {
    .main-filter__toggle {
      display: none;
    }

    .main-filter__search {
      flex: 1;
      min-width: 0;
    }

    .main-filter__wrap--desktop {
      display: flex;
      align-items: stretch;
      gap: 12px;
    }

    .main-filter__wrap-mobile {
      display: none;
    }

    .main-filter__wrap--desktop .main-filter__items {
      display: flex;
      flex: 1;
      gap: 12px;
      flex-wrap: nowrap;
    }

    .main-filter__wrap--desktop .main-filter__item {
      flex: 1 1 0;
      min-width: 0;
    }

    .main-filter__wrap--desktop .main-filter__items:has(.main-filter__item:only-child) .main-filter__item {
      flex: 1 1 100%;
    }
  }
}

.clinics {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.clinic-list {
  &__layout {
    display: grid;
    gap: 16px;
  }

  &__items {
    min-width: 0;

    @include media(sm) {
      padding-bottom: 88px;
    }
  }

  // На мобилке карта и список не живут рядом: вид задаёт тумблер.
  &__layout--list &__map,
  &__layout--map &__items {
    display: none;
  }

  &__map {
    min-height: 320px;
    height: clamp(320px, calc(100dvh - 220px), 640px);
  }

  @include media(md, min-width) {
    &__layout {
      grid-template-columns: minmax(0, 1fr) 360px;
      align-items: start;
    }

    &__layout--no-map {
      grid-template-columns: minmax(0, 1fr);
    }

    &__layout--list &__map,
    &__layout--map &__items {
      display: block;
    }

    // 96px — клиренс липкой шапки, как в SharedVetCardPage.
    // align-self + max-height: колонка не растягивается вместе со списком,
    // иначе sticky не срабатывает и при скролле виден только низ карты.
    &__map {
      --clinic-map-top: calc(96px + var(--promo-banner-offset, 0px));
      --clinic-map-height: calc(100dvh - 112px - var(--promo-banner-offset, 0px));
      position: sticky;
      top: var(--clinic-map-top);
      align-self: start;
      height: var(--clinic-map-height);
      max-height: var(--clinic-map-height);
      overflow: hidden;
    }
  }
}

.loading-overlay {
  background: alpha($color-dark-white, 0.4);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid $color-dark-white;
  border-top: 4px solid $color-purple;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
